import { useRef, useEffect, useState } from 'react';
import type { ElementPatch, TextContentData, TypographyStyle } from '@/components/canvas/document/publicTypes';
import { scaleTextDocumentForResize } from '@/components/canvas/elements/text/model/textResize';

export type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

const CORNER_HANDLES = new Set<ResizeHandle>(['nw', 'ne', 'se', 'sw']);

/**
 * Shift 键等比缩放约束（仅对角手柄生效）。
 *
 * 取自由缩放结果中两轴较大的比例作为统一缩放系数，然后按手柄固定角
 * 重新计算 x / y，保证对角的固定点位置不变。
 *
 * 固定角：
 *   nw → 右下  (x + w, y + h)
 *   ne → 左下  (x,     y + h)
 *   se → 左上  (x,     y    )
 *   sw → 右上  (x + w, y    )
 */
export function applyAspectRatio(
  handle: ResizeHandle,
  startW: number,
  startH: number,
  x: number,
  y: number,
  w: number,
  h: number
): { x: number; y: number; w: number; h: number } {
  if (!CORNER_HANDLES.has(handle)) return { x, y, w, h };

  // 两轴比例中取较大值，使元素始终覆盖鼠标拖拽方向
  const s = Math.max(w / startW, h / startH);
  const newW = Math.max(1, startW * s);
  const newH = Math.max(1, startH * s);

  // 自由缩放后，各手柄对应的固定边已由 x + w / y + h 保存；
  // 用固定边反推新 x / y
  const newX = (handle === 'nw' || handle === 'sw') ? x + w - newW : x;
  const newY = (handle === 'nw' || handle === 'ne') ? y + h - newH : y;

  return { x: newX, y: newY, w: newW, h: newH };
}

export interface ResizeState {
  id: string;
  handle: ResizeHandle;
  startX: number;
  startY: number;
  startW: number;
  startH: number;
  startClientX: number;
  startClientY: number;
  /** 非文本或未传 getMinSize 时使用的固定最小宽高 */
  minWidth?: number;
  minHeight?: number;
  /** 文本四角缩放：起始基准字号。 */
  startFontSize?: number;
  startTypography?: TypographyStyle;
  /** 缩放开始时的 TextDocument 快照 */
  startDocument?: TextContentData['document'];
}

/** 按当前预览尺寸动态返回最小宽高（用于文本：宽度可自由拖，高度不低于当前宽度下内容所需） */
export type GetMinSize = (id: string, currentWidth: number, currentHeight: number) => { minWidth: number; minHeight: number };

export function useResize(
  zoom: number,
  updateElementSilent: (id: string, patch: ElementPatch) => void,
  commitElement: (id: string, patch: ElementPatch) => void,
  isLocked?: (id: string) => boolean,
  getMinSize?: GetMinSize
) {
  const [resizeState, setResizeState] = useState<ResizeState | null>(null);
  const [resizePreview, setResizePreview] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const resizePreviewRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);
  const updateElementSilentRef = useRef(updateElementSilent);
  const commitElementRef = useRef(commitElement);
  const getMinSizeRef = useRef(getMinSize);
  updateElementSilentRef.current = updateElementSilent;
  commitElementRef.current = commitElement;
  getMinSizeRef.current = getMinSize;

  useEffect(() => {
    if (!resizeState) return;
    const {
      id,
      handle,
      startX,
      startY,
      startW,
      startH,
      startClientX,
      startClientY,
      minWidth: stateMinW = 1,
      minHeight: stateMinH = 1,
      startFontSize,
      startTypography,
      startDocument,
    } = resizeState;

    const isTextCorner =
      startFontSize != null && startTypography != null && startDocument != null && CORNER_HANDLES.has(handle);

    const onMove = (e: MouseEvent) => {
      const dCanvasX = (e.clientX - startClientX) / zoom;
      const dCanvasY = (e.clientY - startClientY) / zoom;
      let x = startX, y = startY, w = startW, h = startH;
      if (handle === 'nw') { w = Math.max(1, startW - dCanvasX); h = Math.max(1, startH - dCanvasY); x = startX + startW - w; y = startY + startH - h; }
      else if (handle === 'n') { h = Math.max(1, startH - dCanvasY); y = startY + startH - h; }
      else if (handle === 'ne') { w = Math.max(1, startW + dCanvasX); h = Math.max(1, startH - dCanvasY); y = startY + startH - h; }
      else if (handle === 'e') { w = Math.max(1, startW + dCanvasX); }
      else if (handle === 'se') { w = Math.max(1, startW + dCanvasX); h = Math.max(1, startH + dCanvasY); }
      else if (handle === 's') { h = Math.max(1, startH + dCanvasY); }
      else if (handle === 'sw') { w = Math.max(1, startW - dCanvasX); h = Math.max(1, startH + dCanvasY); x = startX + startW - w; }
      else if (handle === 'w') { w = Math.max(1, startW - dCanvasX); x = startX + startW - w; }

      // 文本四角：强制锁定宽高比（比例不变 → 文字不重排）；其他情况仅在按下 Shift 时锁定
      if (isTextCorner || e.shiftKey) {
        ({ x, y, w, h } = applyAspectRatio(handle, startW, startH, x, y, w, h));
        w = Math.max(1, w);
        h = Math.max(1, h);
      }

      const min = getMinSizeRef.current
        ? getMinSizeRef.current(id, w, h)
        : { minWidth: stateMinW, minHeight: stateMinH };
      w = Math.max(min.minWidth, w);
      h = Math.max(min.minHeight, h);
      const preview = { x, y, width: w, height: h };
      resizePreviewRef.current = preview;
      setResizePreview(preview);

      // 文本四角：字号随缩放比例同步（宽高比已锁定，w/startW === h/startH）
      const patch: ElementPatch = {
        layout: { x: preview.x, y: preview.y },
        size: { width: preview.width, height: preview.height },
      };
      if (isTextCorner) {
        const scale = w / startW;
        patch.typography = {
          fontSize: Math.max(1, Math.round(startFontSize! * scale)),
        };
        patch.data = {
          document: scaleTextDocumentForResize(
            startDocument,
            scale,
            startFontSize!,
          ),
        };
      }
      updateElementSilentRef.current(id, patch);
    };

    const onUp = () => {
      if (resizePreviewRef.current) {
        const patch: ElementPatch = {
          layout: { x: resizePreviewRef.current.x, y: resizePreviewRef.current.y },
          size: {
            width: resizePreviewRef.current.width,
            height: resizePreviewRef.current.height,
          },
        };
        if (isTextCorner) {
          const scale = resizePreviewRef.current.width / startW;
          patch.typography = {
            fontSize: Math.max(1, Math.round(startFontSize! * scale)),
          };
          patch.data = {
            document: scaleTextDocumentForResize(
              startDocument,
              scale,
              startFontSize!,
            ),
          };
        }
        commitElementRef.current(id, patch);
      }
      setResizeState(null);
      setResizePreview(null);
      resizePreviewRef.current = null;
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, [resizeState, zoom]);

  return { resizeState, setResizeState, resizePreview, setResizePreview, resizePreviewRef };
}
